import { useState } from 'react';
import {
  ERROR_CODES,
  dayOfSlot,
  dayStartSlot,
  displayEndOfSlot,
  formatSlotRange,
  slotOfDay,
} from '@betreuung/shared';
import { AlertTriangle } from 'lucide-react';
import { ApiError } from '@/api/http';
import {
  useClearRange,
  useCreateAssignment,
  useDeleteAssignment,
  useUpdateAssignment,
} from '@/api/mutations';
import { ResponsiveModal } from '@/components/shared/ResponsiveModal';
import { Button } from '@/components/ui/button';
import { useAppData } from '@/app/appData';
import { useIsDesktop } from '@/app/useMediaQuery';
import { de } from '@/i18n/de';
import { DateField } from './DateField';
import { PersonRadioCards } from './PersonRadioCards';
import { TimeStepper } from './TimeStepper';
import { useAssignmentFlows } from './useAssignmentFlows';

/**
 * Create and edit dialog — one component for both, as the handoff requires.
 *
 * Saving "Nicht zugewiesen" is not a special case of assigning but a range
 * operation: the server deletes, shortens or splits whatever currently covers
 * the range.
 */
export function AssignmentModal() {
  const { draft, closeDraft, updateDraft, reportSaveError } = useAssignmentFlows();
  const { persons, nowSlot } = useAppData();
  const isDesktop = useIsDesktop();
  const [genericError, setGenericError] = useState<string | null>(null);

  const createAssignment = useCreateAssignment();
  const updateAssignment = useUpdateAssignment();
  const deleteAssignment = useDeleteAssignment();
  const clearRange = useClearRange();

  const pending =
    createAssignment.isPending ||
    updateAssignment.isPending ||
    deleteAssignment.isPending ||
    clearRange.isPending;

  if (!draft) return null;

  const fromDay = dayOfSlot(draft.startSlot);
  const fromSlotInDay = slotOfDay(draft.startSlot);
  const displayEnd = displayEndOfSlot(draft.endSlot);
  const rangeValid = draft.endSlot > draft.startSlot;
  const touchesPast = draft.startSlot < nowSlot;

  const setStart = (dayIndex: number, slotInDay: number) => {
    updateDraft({ startSlot: dayStartSlot(dayIndex) + slotInDay });
  };

  const setEnd = (dayIndex: number, slotInDay: number) => {
    // A slot-in-day of 48 is the same instant as 0 of the following day; the
    // arithmetic handles that without a special case.
    updateDraft({ endSlot: dayStartSlot(dayIndex) + slotInDay });
  };

  const onSave = async () => {
    if (!rangeValid || pending) return;
    setGenericError(null);

    try {
      if (draft.personId === null) {
        await clearRange.mutateAsync({ startSlot: draft.startSlot, endSlot: draft.endSlot });
      } else if (draft.mode === 'edit' && draft.assignmentId !== undefined) {
        await updateAssignment.mutateAsync({
          id: draft.assignmentId,
          personId: draft.personId,
          startSlot: draft.startSlot,
          endSlot: draft.endSlot,
          expectedUpdatedAt: draft.expectedUpdatedAt ?? 0,
        });
      } else {
        await createAssignment.mutateAsync({
          personId: draft.personId,
          startSlot: draft.startSlot,
          endSlot: draft.endSlot,
        });
      }
      closeDraft();
    } catch (error) {
      // A conflict closes the modal and raises its own dialog; anything else is
      // reported in place so the draft is not lost for no reason.
      if (!reportSaveError(error)) setGenericError(saveErrorMessage(error));
    }
  };

  const onDelete = async () => {
    if (draft.assignmentId === undefined || pending) return;
    setGenericError(null);
    try {
      await deleteAssignment.mutateAsync(draft.assignmentId);
      closeDraft();
    } catch (error) {
      if (!reportSaveError(error)) setGenericError(saveErrorMessage(error));
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(next) => {
        if (!next) closeDraft();
      }}
      title={draft.mode === 'edit' ? de.modal.editTitle : de.modal.createTitle}
    >
      {/* The hint is visible, so it must not be repeated as an sr-only
          description — a screen reader would announce it twice. */}
      <p className="pb-3.5 text-hint text-fg-hint">{de.modal.hint}</p>

      {touchesPast ? (
        <p className="mb-3 flex items-start gap-2 rounded-control border border-cal-2 bg-cal-2/10 px-3 py-2 text-label text-fg-secondary">
          <AlertTriangle className="mt-px size-3.5 shrink-0 text-cal-2" />
          {de.pastDialog.bodyWithoutPerson(formatSlotRange(draft.startSlot, draft.endSlot))}
        </p>
      ) : null}

      <div className="grid gap-2">
        <div className="flex items-center gap-2">
          <span className="w-[30px] shrink-0 text-control font-medium text-fg-tertiary">
            {de.modal.from}
          </span>
          <DateField dayIndex={fromDay} onChange={(day) => setStart(day, fromSlotInDay)} />
          <TimeStepper
            slotInDay={fromSlotInDay}
            onChange={(slotInDay) => setStart(fromDay, slotInDay)}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="w-[30px] shrink-0 text-control font-medium text-fg-tertiary">
            {de.modal.to}
          </span>
          <DateField
            dayIndex={displayEnd.dayIndex}
            onChange={(day) => setEnd(day, displayEnd.slotInDay)}
          />
          <TimeStepper
            slotInDay={displayEnd.slotInDay}
            allowEndOfDay
            onChange={(slotInDay) => setEnd(displayEnd.dayIndex, slotInDay)}
          />
        </div>
      </div>
      <p className="pt-1.5 pl-[38px] text-duration text-fg-hint">{de.modal.stepperHint}</p>

      {!rangeValid ? (
        <p role="alert" className="pt-2 text-label text-cal-9">
          {de.modal.invalidRange}
        </p>
      ) : null}

      <p className="section-label pt-4 pb-2">{de.modal.assignTo}</p>
      <PersonRadioCards
        persons={persons.caregivers}
        value={draft.personId}
        onChange={(personId) => updateDraft({ personId })}
      />

      {genericError ? (
        <p role="alert" className="pt-3 text-label text-cal-9">
          {genericError}
        </p>
      ) : null}

      {isDesktop ? (
        <div className="flex items-center justify-between pt-[18px]">
          <span className="text-chip text-fg-hint">{de.modal.logged}</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="dialog" onClick={closeDraft} disabled={pending}>
              {de.modal.cancel}
            </Button>
            <Button size="dialog" onClick={() => void onSave()} disabled={pending || !rangeValid}>
              {pending ? de.modal.saving : de.modal.save}
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid gap-2 pt-4">
          <Button onClick={() => void onSave()} disabled={pending || !rangeValid}>
            {pending ? de.modal.saving : de.modal.save}
          </Button>
          <Button variant="text" size="text" onClick={closeDraft} disabled={pending}>
            {de.modal.cancel}
          </Button>
        </div>
      )}

      {draft.mode === 'edit' ? (
        <div className="flex justify-center pt-1">
          <Button
            variant="destructive"
            size="text"
            className="hit-44"
            onClick={() => void onDelete()}
            disabled={pending}
          >
            {de.modal.delete}
          </Button>
        </div>
      ) : null}
    </ResponsiveModal>
  );
}

/**
 * A save that was refused rather than merely conflicting.
 *
 * The only 403 the assignment endpoints produce is "this account may not write",
 * which happens when a role was revoked while the tab was open. Reporting it as
 * the generic failure would invite a retry that can never succeed.
 */
function saveErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.code === ERROR_CODES.FORBIDDEN) {
    return de.errors.readOnly;
  }
  return de.errors.generic;
}
